/**
 * Wishlist storage.
 *
 * Product ids are kept in localStorage, newest first, mirroring the
 * `RecentlyViewed` helper so both features behave the same way. Everything that
 * shows wishlist state listens for the `wishlist:change` event on `document`.
 */

const STORAGE_KEY = 'flolaeWishlist';
const MAX_PRODUCTS = 100;
const CHANGE_EVENT = 'wishlist:change';

export class Wishlist {
  /** @returns {string} The event name dispatched on `document` after every change. */
  static get EVENT() {
    return CHANGE_EVENT;
  }

  /**
   * The saved product ids, newest first.
   * @returns {string[]}
   */
  static getProducts() {
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      if (!Array.isArray(stored)) return [];
      return stored.filter(Boolean).map(String);
    } catch (error) {
      return [];
    }
  }

  /**
   * @param {string|number} productId
   * @returns {boolean} Whether the product is saved.
   */
  static has(productId) {
    return this.getProducts().includes(String(productId));
  }

  /** @returns {number} How many products are saved. */
  static count() {
    return this.getProducts().length;
  }

  /**
   * @param {string|number} productId
   * @returns {boolean} `true` — the product is saved.
   */
  static add(productId) {
    const id = String(productId);
    const products = [id, ...this.getProducts().filter((stored) => stored !== id)].slice(0, MAX_PRODUCTS);

    this.#persist(products, id, true);
    return true;
  }

  /**
   * @param {string|number} productId
   * @returns {boolean} `false` — the product is no longer saved.
   */
  static remove(productId) {
    const id = String(productId);

    this.#persist(
      this.getProducts().filter((stored) => stored !== id),
      id,
      false
    );
    return false;
  }

  /**
   * @param {string|number} productId
   * @returns {boolean} Whether the product is saved after the toggle.
   */
  static toggle(productId) {
    return this.has(productId) ? this.remove(productId) : this.add(productId);
  }

  static clear() {
    this.#persist([], null, false);
  }

  /**
   * @param {string[]} products
   * @param {string|null} productId - The product that changed, if any.
   * @param {boolean} saved - The new state of that product.
   */
  static #persist(products, productId, saved) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(products));
    } catch (error) {
      // Private browsing or a full quota — the UI still updates for this page view.
    }

    document.dispatchEvent(
      new CustomEvent(CHANGE_EVENT, {
        detail: { productId, saved, count: products.length, products },
      })
    );
  }
}
